<template>
  <LandingSection title="常见问题（FAQs）">
    <XFaq :faq-list="faqList" @contact="$emit('consult-click')" />
  </LandingSection>
</template>

<script setup lang="ts">
import XFaq from '@/design/widgets/faq/faq.vue';
import { useFaqHelper } from '@/design/widgets/faq/useFaqHelper';
import LandingSection from '~/design/widgets/landing-section/cn-template.vue';

const linksConfig = {
  'help-center': 'https://www.youyan3d.com/help-center',
  'what-is-youyan': 'https://www.youyan3d.com/blog/What-Is-Youyan.html',
  'try-youyan-free': 'https://www.youyan3d.com/login',
  'corporate-training': 'https://www.youyan3d.com/scenarios/corporate-training.html',
  pedagogical: 'https://www.youyan3d.com/scenarios/pedagogical.html',
  marketing: 'https://www.youyan3d.com/scenarios/marketing.html',
  'ai-motion-generation':
    'https://www.youyan3d.com/blog/Text-to-3D-Animation-Generate-AI-Digital-Human-Animation-by-Input-Script.html',
  'ai-voice-generation':
    'https://www.youyan3d.com/blog/Supernatural-Multistyle-Text-to-Language-for-Professional-Scenarios.html',
  'aigc-camera': 'https://www.youyan3d.com/blog/Text-to-3D-Camera-Movement-Auto-Generate.html',
  'we-media': 'https://www.youyan3d.com/scenarios/we-media.html',
  case: 'https://www.youyan3d.com/case',
  template: 'https://www.youyan3d.com/template',
  subscribe: 'https://www.youyan3d.com/subscribe',
  'multi-language-tts':
    'https://www.youyan3d.com/blog/Master-Multilingual-Videos-from-Translation-to-Voice.html',
  'easily-make-high-quality-videos':
    'https://www.youyan3d.com/blog/Easily-Make-Hollywood-Level-High-Quality-Videos.html',
  soc2: 'https://www.youyan3d.com/help-center/44-183.html',
};

// 将链接配置转换为 FaqLink 格式
const links = Object.entries(linksConfig).map(([key, url]) => ({ key, url }));

// 使用 FAQ 辅助工具
const { getLink, getStrong, getContactLink } = useFaqHelper(links);

// FAQ 数据列表
const faqList = [
  {
    question: '有言数字人API主要能用来做什么？',
    answer: `有言数字人API将3D数字人视频生成技术封装为可供调用的接口，能一键将您的文本、图片、PPT等内容转化为专业级3D数字人讲解的视频。无论是为在线教育平台生成讲师课程，还是为企业内部制作内培指南，或是为市场部门快速生成营销推广材料，都可以零剪辑基础，实现视频内容的批量、自动化生产。`,
    isOpen: false,
  },
  {
    question: '与其他视频生成API相比，有言API的核心优势是什么？',
    answer: `我们的核心优势在于“全栈式AIGC 3D能力”。与常见的2D卡通头像或拼接真人视频不同，我们提供：
- ${getStrong(getLink('easily-make-high-quality-videos', '低门槛AIGC体验'))}：从脚本到成片，全流程AI驱动，无需任何复杂剪辑与渲染知识。
- ${getStrong('全流程API覆盖')}：从素材上传、视频合成、支持异步处理与状态查询，提供完整接口支持。
- ${getStrong('精细化参数调整')}：2000+超写实3D数字人形象、音色、语速、语调、字幕、分辨率等多种参数灵活配置。
- ${getStrong('高性价比')}：将传统高昂的3D视频制作成本降至极低，支持大规模、高频次的内容生产。`,
    isOpen: false,
  },
  {
    question: '通过API可以生成哪些格式和分辨率的视频？',
    answer: `我们的API输出支持 mp4、mov、webm主流视频格式，并提供多种分辨率选项，包括但不限于720P、1080P乃至4K超高清分辨率，横屏与竖屏均可配置，以满足不同平台（如社交媒体、官网、大屏展示）的发布需求。具体支持的格式和参数请${getContactLink('api-sdk', '联系我们的销售团队')}，获取完整解决方案。`,
    isOpen: false,
  },
  {
    question: '有言API支持多语言和方言播报吗？',
    answer: `我们的${getLink('ai-voice-generation', 'AI语音合成技术')}支持多种音色，支持普通话及${getLink('multi-language-tts', '多国语言')}（如英语、日语、韩语等）及地方方言（如粤语、四川话等）。您可以通过简单的参数指定，生成不同语言和口音的播报视频。我们也在持续扩展小语种库，以满足您的全球化内容创作需求。`,
    isOpen: false,
  },
  {
    question: '有言数字人API是如何计费的？',
    answer: `我们提供灵活、透明的计费方式，主要按“视频生成的时长”计费。提供按量付费与企业订阅两种主要方式，并设有阶梯定价，包括适合初创团队和个人的“按量付费包”，以及适合中大型企业、性价比更高的“订阅包”。您可以根据业务量灵活选择。${getContactLink('api-sdk', '联系我们的销售团队')}，获取详细方案和报价，我们会为您定制最经济的用量方案。`,
    isOpen: false,
  },
  {
    question: '如果我遇到技术问题，可以获得哪些技术支持？',
    answer: `我们提供多层次的技术支持体系：
- ${getLink('help-center', '在线文档与FAQ')}： 自助解决常见问题。
- ${getStrong('工单系统')}： 7x24小时提交问题，由技术团队快速响应。
- ${getStrong('专属技术群')}： 针对企业版客户，我们提供专属技术交流群，提供一对一的高效支持。
- ${getStrong('电话支持')}： 针对紧急问题，提供电话支持通道。`,
    isOpen: false,
  },
  {
    question: '我们的业务数据和安全如何得到保障？',
    answer: `数据安全是我们的生命线。我们通过“基于角色的访问控制、API密钥管理、全链路HTTPS加密传输”等多种技术手段保障调用安全。同时，我们已经通过${getLink('soc2', 'SOC2')}的认证。对于有更高安全要求的客户（如金融、政务），我们可提供私有化部署方案。`,
    isOpen: false,
  },
  {
    question: '我没有很强的技术团队，集成API会不会很复杂？',
    answer: `请放心。我们的API设计遵循RESTful标准，结构清晰、文档完备，并提供了丰富的代码示例。即便您是中小型团队，只要有基础的开发能力，也能在很短时间内顺利完成对接。`,
    isOpen: false,
  },
  {
    question: '除了标准功能，是否支持定制化开发？',
    answer: `是的。如果我们的标准API功能无法完全满足您的特定业务场景，我们拥有专业的技术服务团队，您可以${getContactLink('api-sdk', '联系我们')}为您提供个性化的功能定制开发服务，共创解决方案。`,
    isOpen: false,
  },
];
</script>
